import { captureImage } from './actions/captureImage';
import { setCamera } from './actions/setCamera';
import { setSceneFromId, setSceneFromJson } from './actions/setScene';
import type { TSceneJsonDto } from './domain/scene/sceneJsonDto';
import { store } from './store/appStore';
import { setResolution } from './store/appSlice';
import type { TCamera } from './types/protocolTypes';
import { waitForAppReady, waitForSceneReady } from './appContext';

interface WindowEx extends Window {
  captureImage: (type?: string, quality?: number) => Promise<ArrayBuffer | null>;
  setCamera: (camera: TCamera) => void;
  setResolution: (width: number, height: number) => void;
  setSceneFromId: (id: string) => void;
  setSceneFromJson: (scene: TSceneJsonDto) => void;
  waitForAppReady: () => Promise<void>;
  waitForSceneReady: () => Promise<void>;
}

declare let window: WindowEx;

/**
 * Expose functions to control the application from global window scope.
 */
export const init = () => {
  /**
   * Capture image.
   * @param type Image format mime.
   * @param quality Image format compression quality.
   */
  window.captureImage = (type?: string, quality?: number): Promise<ArrayBuffer | null> => {
    const viewerRef = store.getState().appRefs.viewerRef;

    if (!viewerRef?.current) {
      return Promise.resolve(null);
    }

    return captureImage(viewerRef.current, type, quality).then(blob => {
      return blob ? blob.arrayBuffer() : null;
    });
  };

  /**
   * Set camera from camera desc.
   * @param camera Camera descriptor.
   */
  window.setCamera = (camera: TCamera) => {
    const viewerRef = store.getState().appRefs.viewerRef;

    if (!viewerRef?.current) {
      return;
    }

    setCamera(viewerRef.current, camera);
  };

  /**
   * Set resolution.
   * @param width Width.
   * @param height Height.
   */
  window.setResolution = (width: number, height: number) => {
    store.dispatch(setResolution({ x: width, y: height }));
  };

  /**
   * Set scene from ID.
   * @param id Scene ID.
   */
  window.setSceneFromId = (id: string) => {
    setSceneFromId(store.dispatch, id);
  };

  /**
   * Set scene from JSON.
   * @param scene Scene JSON.
   */
  window.setSceneFromJson = (scene: TSceneJsonDto) => {
    setSceneFromJson(store.dispatch, scene);
  };

  /**
   * Returns a promise that resolves when the app is ready.
   */
  window.waitForAppReady = waitForAppReady;

  /**
   * Returns a promise that resolves when the scene is ready.
   */
  window.waitForSceneReady = waitForSceneReady;
};
